import React, { Suspense, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  getAllCycles,
  GetEmployeeAppraisals,
  getSelfAppraisals,
  GetSkipLevelAppraisals,
  GetTeamAppraisals,
} from "./PMSService";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { TostMsg } from "../../../App";
import ComponentLoader from "../../../Shared/Sidebar/ComponentLoader";

const tabs = [
  { key: "all", label: "All" },
  { key: "employeepending", label: "EmployeePending" },
  { key: "managerpending", label: "ManagerPending" },
  { key: "skiplevelpending", label: "SkiplevelPending" },
  { key: "normalizationpending", label: "NormalizationPending" },
];



/* -------------------------------------------------------
   Helper Functions
------------------------------------------------------- */

const getInitials = (name = "") => {
  if (!name) return "--";

  const words = String(name).trim().split(/\s+/).filter(Boolean);

  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }

  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
};

const formatDate = (date) => {
  if (!date) return "Pending";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const STAGE_IDS = {
  SELF_ASSESSMENT: 1,
  MANAGER_REVIEW: 2,
  SKIP_LEVEL_REVIEW: 3,
  NORMALIZATION: 4,
  CLOSED: 5,
};

const normalizeStatus = (stageId) => {
  const stage = Number(stageId);

  switch (stage) {
    case STAGE_IDS.SELF_ASSESSMENT:
      return "Waiting for Employee";

    case STAGE_IDS.MANAGER_REVIEW:
      return "Pending Your Review";

    case STAGE_IDS.SKIP_LEVEL_REVIEW:
      return "Pending Your Review";

    case STAGE_IDS.NORMALIZATION:
      return "Waiting for Manager";

    case STAGE_IDS.CLOSED:
      return "Reviewed";

    default:
      return "Pending Your Review";
  }
};

const getStatusBadgeClass = (stageId, isOverdue = false) => {
  const stage = Number(stageId);

  if (isOverdue) {
    return "badge-overdue";
  }

  switch (stage) {
    case STAGE_IDS.SELF_ASSESSMENT:
      return "badge-created";

    case STAGE_IDS.MANAGER_REVIEW:
      return "badge-self";

    case STAGE_IDS.SKIP_LEVEL_REVIEW:
      return "badge-self";

    case STAGE_IDS.NORMALIZATION:
      return "badge-created";

    case STAGE_IDS.CLOSED:
      return "badge-completed";

    default:
      return "badge-self";
  }
};

const getActionLabel = (stageId) => {
  const stage = Number(stageId);

  switch (stage) {
    case STAGE_IDS.SELF_ASSESSMENT:
      return "Locked";

    case STAGE_IDS.MANAGER_REVIEW:
      return "Review";

    case STAGE_IDS.SKIP_LEVEL_REVIEW:
      return "View";

    case STAGE_IDS.NORMALIZATION:
      return "View";

    case STAGE_IDS.CLOSED:
      return "View";

    default:
      return "Review";
  }
};

const getActionIcon = (stageId, isOverdue = false) => {
  const stage = Number(stageId);

  if (isOverdue) {
    return "fa-pen-to-square";
  }

  switch (stage) {
    case STAGE_IDS.SELF_ASSESSMENT:
      return "fa-lock";

    case STAGE_IDS.MANAGER_REVIEW:
      return "fa-pen-to-square";

    case STAGE_IDS.SKIP_LEVEL_REVIEW:
      return "fa-eye";

    case STAGE_IDS.NORMALIZATION:
      return "fa-eye";

    case STAGE_IDS.CLOSED:
      return "fa-eye";

    default:
      return "fa-pen-to-square";
  }
};

const getActionVariant = (stageId) => {
  const stage = Number(stageId);

  if (stage === STAGE_IDS.CLOSED) {
    return "secondary";
  }

  return "primary";
};

const getActionStyle = (stageId, isOverdue = false) => {
  const stage = Number(stageId);

  if (isOverdue) {
    return {
      background: "#e53e3e",
      color: "#fff",
    };
  }

  if (stage === STAGE_IDS.SELF_ASSESSMENT) {
    return {
      opacity: 0.4,
      cursor: "not-allowed",
    };
  }

  return {};
};

/* -------------------------------------------------------
   Component
------------------------------------------------------- */

const PMSTeamLevelList = () => {
  const [selectedCycle, setSelectedCycle] = useState("");

  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
 

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const history = useHistory();
  const { guid } = useParams();
  const { toster } = useContext(TostMsg);
  const [appraisals, setAppraisals] = useState([]);
  const [teamLevelSummary, setTeamLevelSummary] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
const [pageSize, setPageSize] = useState(10);
const [totalRecords, setTotalRecords] = useState(0);

const totalPages = Math.max(
  1,
  Math.ceil(Number(totalRecords || 0) / Number(pageSize || 10))
);

  /* -------------------------------------------------------
     Get Skip Level Appraisals
  ------------------------------------------------------- */

  useEffect(() => {
    fetchAppraisals();
    // getAppraisalDetail();
  }, []);

useEffect(() => {
  fetchTeamLevelAppraisals();
}, [
  selectedCycle,
  pageNumber,
  pageSize,
  activeTab,
  searchTerm
]);

const handleTabChange = (tabKey) => {
  setActiveTab(tabKey);
  setPageNumber(1);
};

const handleSearchChange = (e) => {
  setSearchTerm(e.target.value);
  setPageNumber(1);
};



const handlePageSizeChange = (e) => {
  setPageSize(Number(e.target.value));
  setPageNumber(1);
};

  const getAppraisalDetail = async () => {
    try {
      if (!guid) {
        console.error("Assessment GUID not found.");
        return null;
      }

      const params = {
        Guid: guid,
      };

      const response = await GetEmployeeAppraisals(params);
      const responseRoot = response?.Data ?? response;

      setSelectedCycle(responseRoot?.CycleId);
    } catch (error) {
      console.error("Get appraisal detail error:", error);

      toster?.current?.error(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "Failed to load appraisal details.",
      );

      return null;
    }
  };

const fetchTeamLevelAppraisals = async () => {
  if (!selectedCycle) {
    setRows([]);
    setTotalRecords(0);
    return;
  }

 const stageFilterMap = {
  employeepending: 1,
  managerpending: 2,
  skiplevelpending: 3,
  normalizationpending: 4,
};

const stageFilter =
  activeTab === "all"
    ? ""
    : stageFilterMap[activeTab] || "";

  const params = {
    CycleId: Number(selectedCycle),
    StageFilter: stageFilter,
    Search: searchTerm?.trim() || "",
    Department:"",
    PageNumber: pageNumber,
    PageSize: pageSize,
  };

  try {
    setLoading(true);

    const response = await GetTeamAppraisals(params);

    const responseData = response?.Data ?? response;

    const data = Array.isArray(responseData?.Items)
      ? responseData.Items
      : Array.isArray(responseData)
      ? responseData
      : [];

    // Get total count from API
    const total = 
      responseData?.Summary?.Total ??
      0;

    setTotalRecords(Number(total));

    // Summary
    const summary = responseData?.Summary;
    setTeamLevelSummary(summary);

   const mappedRows = data.map((item) => {
  const employeeName = item?.EmployeeName || "Unknown Employee";
  const managerName = item?.ManagerName || "-";

  const stageId = Number(item?.StageId);

  const stageName = getStageName(
    stageId,
    item?.StageName
  );

  const isOverdue = item?.IsOverdue === 1;

  const status = getStatus(
    stageId,
    isOverdue
  );

  const selfSubmitted = item?.SelfSubmittedOn
    ? formatDate(item.SelfSubmittedOn)
    : "Pending";

  const managerReviewed = item?.ManagerReviewedOn
    ? formatDate(item.ManagerReviewedOn)
    : "Pending";

  const score =
    item?.ComputedScore !== null &&
    item?.ComputedScore !== undefined &&
    item?.ComputedScore !== ""
      ? Number(item.ComputedScore).toFixed(1)
      : "—";

  const actionDisabled =
    stageId === STAGE_IDS.SELF_ASSESSMENT ||
    !item?.AppraisalGUID;

  return {
    id: item?.AppraisalId,
    appraisalId: item?.AppraisalId,
    appraisalGuid: item?.AppraisalGUID,
    employeeGuid: item?.EmployeeGUID,
    empId: item?.EmpId,

    name: employeeName,
    initials: getInitials(employeeName),
    role: item?.Designation || "N/A",
    department: item?.Department || "N/A",
    profileImage: item?.ProfileImage || "",

    managerName,
    managerInitials: getInitials(managerName),

    selfSubmitted,
    managerReviewed,

    score,

    // Workflow information
    stageId,
    stageName,

    // UI status
    status,

    // UI styling/actions
    badgeClass: getStatusBadgeClass(
      stageId,
      isOverdue
    ),

    actionLabel: getActionLabel(
      stageId,
      isOverdue
    ),

    actionIcon: getActionIcon(
      stageId,
      isOverdue
    ),

    actionVariant: getActionVariant(
      stageId
    ),

    actionDisabled,

    actionStyle: getActionStyle(
      stageId,
      isOverdue
    ),

    actionPath: item?.AppraisalGUID
      ? `/Assessment/Review/${item.AppraisalGUID}`
      : null,

    isOverdue,

    normalizationActorName:
      item?.NormalizationActorName,

    normalizationInitials:
      getInitials(item?.NormalizationActorName),

    skipLevelName:
      item?.SkipLevelName,

    skipLevelInitials:
      getInitials(item?.SkipLevelName),
  };
});

    setRows(mappedRows);

    // Safety: if current page becomes invalid after filtering
    const calculatedTotalPages = Math.max(
      1,
      Math.ceil(Number(total || 0) / Number(pageSize))
    );

    if (pageNumber > calculatedTotalPages) {
      setPageNumber(calculatedTotalPages);
    }
  } catch (error) {
    console.error("Error fetching team appraisals:", error);
    setRows([]);
    setTotalRecords(0);
  } finally {
    setLoading(false);
  }
};

const getStatus = (stageId, isOverdue = false) => {
  const stage = Number(stageId);

  if (isOverdue) {
    return "Overdue";
  }

  switch (stage) {
    case STAGE_IDS.SELF_ASSESSMENT:
      return "Waiting for Employee";

    case STAGE_IDS.MANAGER_REVIEW:
      return "Pending Your Review";

    case STAGE_IDS.SKIP_LEVEL_REVIEW:
      return "Pending Your Review";

    case STAGE_IDS.NORMALIZATION:
      return "Waiting for Manager";

    case STAGE_IDS.CLOSED:
      return "Reviewed";

    default:
      return "Pending Your Review";
  }
};

const getStageName = (stageId, stageName) => {
  if (stageName) {
    return stageName;
  }

  const stage = Number(stageId);

  switch (stage) {
    case STAGE_IDS.SELF_ASSESSMENT:
      return "Self Assessment";

    case STAGE_IDS.MANAGER_REVIEW:
      return "Manager Review";

    case STAGE_IDS.SKIP_LEVEL_REVIEW:
      return "Skip-Level Review";

    case STAGE_IDS.NORMALIZATION:
      return "Normalization";

    case STAGE_IDS.CLOSED:
      return "Closed";

    default:
      return "Unknown";
  }
};

  const fetchAppraisals = async () => {
    setLoading(true);

    try {
      const response = await getAllCycles();

      const data = response?.Data || response?.result || [];

      setAppraisals(data);
      setSelectedCycle(data[0]?.Id)
    } catch (err) {
      console.error("getSelfAppraisals error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCycleChange = (e) => {
    const cycleId = e.target.value;

    setSelectedCycle(cycleId);
  };

  /* -------------------------------------------------------
     Dynamic Department Options
  ------------------------------------------------------- */

  const departmentOptions = useMemo(() => {
    const departments = rows
      .map((row) => row.department)
      .filter(
        (department) =>
          department && department !== "-" && department !== "N/A",
      );

    return ["All Departments", ...Array.from(new Set(departments))];
  }, [rows]);

  /* -------------------------------------------------------
     Dynamic Tab Counts
  ------------------------------------------------------- */

  const tabCounts = useMemo(() => {
    return {
      all: teamLevelSummary?.Total,//rows.length,

      employeepending: teamLevelSummary?.SelfPending,

      managerpending: teamLevelSummary?.ManagerPending,

      skiplevelpending : teamLevelSummary?.SkipLevelPending,

      normalizationpending: teamLevelSummary?.NormalizationPending,
    };
  }, [rows]);

 
  /* -------------------------------------------------------
     Summary Stats
  ------------------------------------------------------- */

  const stats = useMemo(() => {
    return {
      total: rows.length,

      pendingReview: rows.filter((row) => row.status === "Pending Your Review")
        .length,

      reviewedByYou: rows.filter((row) => row.status === "Reviewed").length,

      overdue: rows.filter((row) => row.status === "Overdue").length,
    };
  }, [rows]);

  /* -------------------------------------------------------
     Open Appraisal
  ------------------------------------------------------- */

  const handleAction = (row) => {
    if (row.actionDisabled || !row.actionPath) {
      return;
    }

    history.push(row.actionPath);
  };

  /* -------------------------------------------------------
     Render
  ------------------------------------------------------- */

  return (
    <div className="container-fluid">
      <div className="row g-3 assessment-module">
        <div className="og-main">
          {/* -----------------------------------------------
              Header
          ------------------------------------------------ */}

          <div className="og-page-header">
            <div>
              <div className="og-breadcrumb">
                <a
                  
                  style={{
                    color: "#EC4119",
                    textDecoration: "none",
                  }}
                >
                  Performance
                </a>{" "}
                › Team Level Reviews
              </div>

              <div className="og-page-title">
                Team Appraisals — Team Level Review
              </div>

              <div className="og-page-sub">
                {selectedCycle} · Your team level review queue
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
              }}
            >
              <select
                className="og-select"
                style={{
                  width: 210,
                  fontSize: 12,
                }}
                value={selectedCycle}
                onChange={handleCycleChange}
              >
                {Array.isArray(appraisals) &&
                  appraisals.map((cycle) => (
                    <option
                      key={cycle?.Id }
                      value={cycle?.Id}
                    >
                      {cycle?.Title || "Untitled Cycle"}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* -----------------------------------------------
              Summary Strip
          ------------------------------------------------ */}

          <div
            className="og-kpi-grid"
            style={{
              gridTemplateColumns: "repeat(6,1fr)",
              marginBottom: 20,
            }}
          >
            <div className="og-kpi">
              <div className="og-kpi-label">Total to Review</div>

              <div className="og-kpi-value">{teamLevelSummary?.Total}</div>

              {/* <div className="og-kpi-sub">In your queue</div> */}
            </div>

            <div className="og-kpi">
              <div className="og-kpi-label">Self Pending</div>

              <div className="og-kpi-value">{teamLevelSummary?.SelfPending}</div>

              {/* <div className="og-kpi-sub">In your queue</div> */}
            </div>

            <div className="og-kpi">
              <div className="og-kpi-label">Manager Review Pending</div>

              <div className="og-kpi-value">{teamLevelSummary?.ManagerPending}</div>

              {/* <div className="og-kpi-sub">In your queue</div> */}
            </div>

            <div className="og-kpi">
              <div className="og-kpi-label" style={{ color: "#d97706" }}>
              Skip-level Pending Review
              </div>

              <div className="og-kpi-value" style={{ color: "#d97706" }}>
              {teamLevelSummary?.SkipLevelPending}
              </div>

              {/* <div className="og-kpi-sub">Manager review done</div> */}
            </div>

            <div className="og-kpi">
              <div className="og-kpi-label" style={{ color: "#276749" }}>
                Normalization Actor Pending
              </div>

              <div className="og-kpi-value" style={{ color: "#276749" }}>
                {teamLevelSummary?.NormalizationPending}
              </div>

              {/* <div className="og-kpi-sub">Sent to normalization</div> */}
            </div>

            <div className="og-kpi">
              <div className="og-kpi-label" style={{ color: "#9b2c2c" }}>
               Completed
              </div>

              <div className="og-kpi-value" style={{ color: "#9b2c2c" }}>
               {teamLevelSummary?.Completed}
              </div>

              {/* <div className="og-kpi-sub">Past deadline</div> */}
            </div>
          </div>

          {/* -----------------------------------------------
              Main Card
          ------------------------------------------------ */}

          <div className="og-card">
            {/* ---------------------------------------------
                Filters
            ---------------------------------------------- */}

            <div
              style={{
                display: "flex",
                gap: 10,
                marginBottom: 14,
                alignItems: "center",
              }}
            >
              {/* Tabs */}

              <div
                style={{
                  display: "flex",
                  gap: 0,
                  border: "1px solid #e9ecf0",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={`btn-og ${
                      activeTab === tab.key
                        ? "btn-og-primary"
                        : "btn-og-secondary"
                    } btn-og-sm`}
                    style={{
                      borderRadius: 0,
                    }}
                    onClick={() => handleTabChange(tab.key)}
                  >
                    {tab.label} ({tabCounts[tab.key]})
                  </button>
                ))}
              </div>

              {/* Search */}

              <input
                className="og-input"
                type="text"
                placeholder="Search employee..."
                style={{ flex: 1 }}
                value={searchTerm}
                onChange={handleSearchChange}
              />

            
            </div>

            {/* ---------------------------------------------
                Table
            ---------------------------------------------- */}

            <div className="og-table-wrap table-responsive">
              <table className="og-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Reporting Manager</th>
                    <th>Skip level Reviewer</th>
                    <th>Normalization Actor</th>
                    {/* <th>Self Submitted</th> */}
                    <th>Manager Reviewed</th>
                    <th>Manager Score</th>
                    {/* <th>Your Status</th> */}
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {/* Loading */}

                  {loading && (
                    <tr>
                      <td
                        colSpan="8"
                        style={{
                          textAlign: "center",
                          padding: 30,
                        }}
                      >
                        Loading skip-level appraisals...
                      </td>
                    </tr>
                  )}

                  {/* Empty */}

                  {!loading && rows.length === 0 && (
                    <tr>
                      <td
                        colSpan="8"
                        style={{
                          textAlign: "center",
                          padding: 30,
                          color: "#8492a6",
                        }}
                      >
                        No team-level appraisals found.
                      </td>
                    </tr>
                  )}

                  {/* Rows */}

                  {!loading &&
                    rows.map((row) => (
                      <tr key={row.id} style={row.rowStyle || {}}>
                        {/* Employee */}

                        <td>
                          <div className="og-emp-cell">
                            <div
                              className="og-emp-av"
                              style={{
                                background: "#EC4119",
                              }}
                            >
                              {row.initials}
                            </div>

                            <div>
                              <div
                                style={{
                                  fontWeight: 600,
                                }}
                              >
                                {row.name}
                              </div>

                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#8492a6",
                                }}
                              >
                                {row.role}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Department */}

                        <td>{row.department}</td>

                        {/* Reporting Manager */}

                        <td>
                          <div className="og-emp-cell">
                            <div
                              className="og-emp-av"
                              style={{
                                background: "#2b6cb0",
                                width: 22,
                                height: 22,
                                fontSize: 9,
                              }}
                            >
                              {row.managerInitials}
                            </div>

                            <span
                              style={{
                                fontSize: 12,
                              }}
                            >
                              {row.managerName}
                            </span>
                          </div>
                        </td>

                          <td>
                          <div className="og-emp-cell">
                            <div
                              className="og-emp-av"
                              style={{
                                background: "#2b6cb0",
                                width: 22,
                                height: 22,
                                fontSize: 9,
                              }}
                            >
                              {row.skipLevelInitials}
                            </div>

                            <span
                              style={{
                                fontSize: 12,
                              }}
                            >
                              {row.skipLevelName}
                            </span>
                          </div>
                        </td>

                          <td>
                          <div className="og-emp-cell">
                            <div
                              className="og-emp-av"
                              style={{
                                background: "#2b6cb0",
                                width: 22,
                                height: 22,
                                fontSize: 9,
                              }}
                            >
                              {row.normalizationInitials}
                            </div>

                            <span
                              style={{
                                fontSize: 12,
                              }}
                            >
                              {row.normalizationActorName}
                            </span>
                          </div>
                        </td>

                        {/* Self Submitted */}

                        {/* <td>
                          <span
                            style={{
                              fontSize: 12,
                              color:
                                row.selfSubmitted === "Pending"
                                  ? "#8492a6"
                                  : "#276749",
                            }}
                          >
                            {row.selfSubmitted !== "Pending" && (
                              <i className="fa fa-check"></i>
                            )}{" "}
                            {row.selfSubmitted}
                          </span>
                        </td> */}

                        {/* Manager Reviewed */}

                        <td>
                          <span
                            style={{
                              fontSize: 12,
                              color:
                                row.managerReviewed === "Pending"
                                  ? "#8492a6"
                                  : "#276749",
                            }}
                          >
                            {row.managerReviewed !== "Pending" && (
                              <i className="fa fa-check"></i>
                            )}{" "}
                            {row.managerReviewed}
                          </span>
                        </td>

                        {/* Manager Score */}

                        <td>
                          <strong>{row.score}</strong>

                          {row.score !== "—" && (
                            <span
                              style={{
                                color: "#8492a6",
                                fontSize: 11,
                              }}
                            >
                              /5
                            </span>
                          )}
                        </td>

                        {/* Status */}

                        {/* <td>
                          <span className={`badge-og ${row.badgeClass}`}>
                            {row.status}
                          </span>
                        </td> */}

                        {/* Action */}

                        <td>
                          <button
                            className={`btn-og btn-og-${row.actionVariant} btn-og-sm`}
                            style={row.actionStyle}
                            onClick={() => handleAction(row)}
                            disabled={row.actionDisabled}
                          >
                            <i className={`fa ${row.actionIcon}`}></i>{" "}
                            {row.actionLabel}
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

 
            </div>

                         {!loading && totalRecords > 0 && (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: 16,
      paddingTop: 14,
      borderTop: "1px solid #e9ecf0",
      gap: 15,
      flexWrap: "wrap",
    }}
  >
    {/* Showing text */}
    <div
      style={{
        fontSize: 12,
        color: "#8492a6",
      }}
    >
      Showing{" "}
      <strong>
        {(pageNumber - 1) * pageSize + 1}
      </strong>{" "}
      to{" "}
      <strong>
        {Math.min(pageNumber * pageSize, totalRecords)}
      </strong>{" "}
      of{" "}
      <strong>{totalRecords}</strong>
    </div>

    {/* Page size */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 12,
        color: "#8492a6",
      }}
    >
      <span>Rows per page:</span>

      <select
        className="og-select"
        value={pageSize}
        onChange={handlePageSizeChange}
        style={{
          width: 75,
          height: 34,
          fontSize: 12,
        }}
      >
        <option value={5}>5</option>
        <option value={10}>10</option>
        <option value={20}>20</option>
        <option value={50}>50</option>
        <option value={100}>100</option>
      </select>
    </div>

    {/* Pagination */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {/* Previous */}
      <button
        type="button"
        className="btn-og btn-og-secondary btn-og-sm"
        disabled={pageNumber === 1}
        onClick={() =>
          setPageNumber((prev) => Math.max(1, prev - 1))
        }
        style={{
          minWidth: 36,
          height: 34,
          padding: "0 10px",
          opacity: pageNumber === 1 ? 0.5 : 1,
          cursor:
            pageNumber === 1
              ? "not-allowed"
              : "pointer",
        }}
      >
        <i className="fa fa-chevron-left"></i>
      </button>

      {/* Page numbers */}
      {Array.from(
        { length: totalPages },
        (_, index) => index + 1
      )
        .filter((page) => {
          if (totalPages <= 5) return true;

          return (
            page === 1 ||
            page === totalPages ||
            Math.abs(page - pageNumber) <= 1
          );
        })
        .map((page, index, pages) => {
          const previousPage = pages[index - 1];

          return (
            <React.Fragment key={page}>
              {previousPage &&
                page - previousPage > 1 && (
                  <span
                    style={{
                      padding: "0 5px",
                      color: "#8492a6",
                    }}
                  >
                    ...
                  </span>
                )}

              <button
                type="button"
                className={`btn-og btn-og-sm ${
                  page === pageNumber
                    ? "btn-og-primary"
                    : "btn-og-secondary"
                }`}
                onClick={() => setPageNumber(page)}
                style={{
                  minWidth: 34,
                  height: 34,
                  padding: "0 8px",
                }}
              >
                {page}
              </button>
            </React.Fragment>
          );
        })}

      {/* Next */}
      <button
        type="button"
        className="btn-og btn-og-secondary btn-og-sm"
        disabled={pageNumber >= totalPages}
        onClick={() =>
          setPageNumber((prev) =>
            Math.min(totalPages, prev + 1)
          )
        }
        style={{
          minWidth: 36,
          height: 34,
          padding: "0 10px",
          opacity:
            pageNumber >= totalPages ? 0.5 : 1,
          cursor:
            pageNumber >= totalPages
              ? "not-allowed"
              : "pointer",
        }}
      >
        <i className="fa fa-chevron-right"></i>
      </button>
    </div>
  </div>
)}
          </div>
        </div>
      </div>

      {loading ? (
        <Suspense>
          <ComponentLoader />
        </Suspense>
      ) : (
        ""
      )}
    </div>
  );
};

export default PMSTeamLevelList;
